import PasswordInput from "@/components/PasswordInput";
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Lightning, Key, EnvelopeSimple, Lock, User as UserIcon, ArrowRight, ShieldCheck, Fingerprint } from "@phosphor-icons/react";
import { usePillars, pillarChipStyle } from "@/contexts/PillarsContext";
import { BG_STYLES } from "@/pages/WelcomePage";

export default function LoginPage() {
  const { login, passkeyLogin, register, tokenLogin, studentLogin, setUser, verifyMfa, sendMfaEmailOtp } = useAuth();
  const { pillars, termPlural, settings, settingsLoaded } = usePillars();
  const logoUrl = settings?.logo_url;
  const brandName = settings?.cover_title || "The Self-Agency Project";
  const [mode, setMode] = useState(
    new URLSearchParams(window.location.search).get("mode") === "register" ? "register" : "login"
  ); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [err, setErr] = useState("");
  const [tokenErr, setTokenErr] = useState("");
  // MFA challenge state
  const [mfaToken, setMfaToken] = useState("");
  const [mfaMethod, setMfaMethod] = useState("totp"); // totp | backup | email
  const [mfaCode, setMfaCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaErr, setMfaErr] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  // Forgot password/code panel
  const [pwForgotOpen, setPwForgotOpen] = useState(false);
  const [forgotMode, setForgotMode] = useState("email"); // email | student
  const [pwForgotEmail, setPwForgotEmail] = useState("");
  const [pwForgotMsg, setPwForgotMsg] = useState("");
  const [pwForgotBusy, setPwForgotBusy] = useState(false);
  const [fFirst, setFFirst] = useState("");
  const [fLast, setFLast] = useState("");
  const [fHints, setFHints] = useState(null);
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from;
  const dest = from ? `${from.pathname}${from.search || ""}` : "/dashboard";
  const goHome = () => nav(dest, { replace: true });

  const onForgotPassword = async (e) => {
    e.preventDefault();
    setPwForgotBusy(true); setPwForgotMsg("");
    try {
      const { data } = await api.post("/auth/forgot-password", {
        email: (pwForgotEmail || email).trim().toLowerCase(),
        origin_url: window.location.origin,
      });
      setPwForgotMsg(data.message || "If an account exists, we've sent a reset link.");
    } catch (e) { setPwForgotMsg(formatApiError(e)); }
    finally { setPwForgotBusy(false); }
  };

  const onForgotCode = async (e) => {
    e.preventDefault();
    setPwForgotBusy(true); setFHints(null); setPwForgotMsg("");
    try {
      const { data } = await api.post("/auth/student-forgot-id", {
        first_name: fFirst.trim(), last_name: fLast.trim(),
      });
      setFHints(data);
    } catch (e) { setPwForgotMsg(formatApiError(e)); }
    finally { setPwForgotBusy(false); }
  };

  const onCred = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (mode === "login" && code.trim()) {
        const res = await studentLogin("", "", code.trim().toUpperCase());
        if (res?.first_login && res?.user) setUser(res.user);
        goHome();
        return;
      }
      if (mode === "login") {
        const res = await login(email.trim().toLowerCase(), password);
        if (res && res.mfa_required) {
          setMfaToken(res.mfa_token);
          setMfaMethod("totp"); setMfaCode(""); setEmailSent(false); setMfaErr("");
          return;
        }
      } else {
        await register(firstName.trim(), lastName.trim(), email.trim().toLowerCase(), password);
      }
      goHome();
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  const onVerifyMfa = async (e) => {
    e.preventDefault();
    setMfaErr(""); setMfaBusy(true);
    try {
      await verifyMfa(mfaToken, mfaMethod, mfaCode.trim(), trustDevice);
      goHome();
    } catch (e) { setMfaErr(formatApiError(e)); }
    finally { setMfaBusy(false); }
  };

  const onSendEmail = async () => {
    setMfaErr(""); setMfaBusy(true);
    try {
      await sendMfaEmailOtp(mfaToken);
      setMfaMethod("email"); setMfaCode(""); setEmailSent(true);
    } catch (e) { setMfaErr(formatApiError(e)); }
    finally { setMfaBusy(false); }
  };

  const cancelMfa = () => { setMfaToken(""); setMfaCode(""); setPassword(""); setMfaErr(""); };

  const onToken = async (e) => {
    e.preventDefault();
    setTokenErr(""); setTokenLoading(true);
    try {
      await tokenLogin(code.trim().toUpperCase());
      goHome();
    } catch (e) { setTokenErr(e.message); }
    finally { setTokenLoading(false); }
  };

  const pageBgStyle = settings?.cover_background_url
    ? { backgroundImage: `linear-gradient(rgba(220,222,226,0.55), rgba(220,222,226,0.85)), url(${settings.cover_background_url})`, backgroundSize: "cover", backgroundPosition: "center" }
    : (BG_STYLES[settings?.cover_background_style] || BG_STYLES.granite);

  return (
    <div className="min-h-screen" style={pageBgStyle}>
    <div className="min-h-screen px-5 py-10 grid lg:grid-cols-2 gap-10 items-center max-w-7xl mx-auto">
      {/* Brand / hero panel */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="hidden lg:flex flex-col gap-6 pr-10"
      >
        <div className="flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} data-testid="login-logo-image" className="h-14 w-auto max-w-[220px] object-contain" />
          ) : !settingsLoaded ? (
            <div className="w-12 h-12" />
          ) : (
            <div className="w-12 h-12 rounded-2xl bg-[#CCFF00] text-[#000000] grid place-items-center font-extrabold text-2xl font-display">E</div>
          )}
          <span className="brand-text text-3xl">{brandName}</span>
        </div>
        <h1 className="text-5xl xl:text-6xl font-extrabold leading-[1.05] font-display">
          <span className="brand-text" data-testid="login-tagline" style={settings?.cover_mission_bold ? { fontWeight: 800 } : undefined}>{settings?.cover_mission || "Train sharper. Level up faster."}</span>
        </h1>
        {pillars.length > 0 && (
          <div className="flex gap-3 mt-2 flex-wrap">
            {pillars.map((p) => (
              <div key={p.slug} className="chip" style={pillarChipStyle(p.color)}>{p.name}</div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Auth panel */}
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
        className="flex flex-col gap-5"
      >
        <div className="card">
          {mfaToken ? (
            <div data-testid="mfa-challenge">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={22} weight="duotone" color="#CCFF00" />
                <h2 className="text-2xl font-bold font-display">Two-factor verification</h2>
              </div>
              <p className="text-[#000000] text-sm mb-5">
                {mfaMethod === "backup"
                  ? "Enter one of your saved backup codes."
                  : mfaMethod === "email"
                    ? (emailSent ? "We emailed you a 6-digit code. Enter it below." : "Send a code to your email.")
                    : "Enter the 6-digit code from your authenticator app."}
              </p>
              <form onSubmit={onVerifyMfa} className="flex flex-col gap-3">
                <input
                  data-testid="mfa-code-input"
                  className={"input-base text-center text-lg " + (mfaMethod === "backup" ? "font-mono tracking-[0.2em] uppercase" : "font-mono tracking-[0.4em]")}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder={mfaMethod === "backup" ? "XXXX-XXXX" : "123456"}
                  inputMode={mfaMethod === "backup" ? "text" : "numeric"}
                  autoFocus required
                />
                <label className="flex items-center gap-2 text-sm text-[#3F3F46] cursor-pointer">
                  <input data-testid="mfa-trust-device" type="checkbox" className="w-4 h-4 accent-[#84CC16]" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} />
                  Trust this device for 30 days
                </label>
                {mfaErr && <div data-testid="mfa-error" className="text-[#FF5100] text-sm">{mfaErr}</div>}
                <button data-testid="mfa-verify-button" type="submit" className="btn-primary mt-1 flex items-center justify-center gap-2" disabled={mfaBusy}>
                  {mfaBusy ? "Verifying..." : "Verify & continue"}<ArrowRight size={18} weight="bold" />
                </button>
              </form>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-sm">
                {mfaMethod !== "totp" && (
                  <button data-testid="mfa-use-totp" className="btn-ghost text-[#000000] underline-offset-2 hover:underline" onClick={() => { setMfaMethod("totp"); setMfaCode(""); setMfaErr(""); }}>Use authenticator app</button>
                )}
                {mfaMethod !== "backup" && (
                  <button data-testid="mfa-use-backup" className="btn-ghost text-[#000000] underline-offset-2 hover:underline" onClick={() => { setMfaMethod("backup"); setMfaCode(""); setMfaErr(""); }}>Use a backup code</button>
                )}
                <button data-testid="mfa-use-email" className="btn-ghost text-[#000000] underline-offset-2 hover:underline" onClick={onSendEmail} disabled={mfaBusy}>Email me a code</button>
                <button data-testid="mfa-cancel" className="btn-ghost text-[#000000] underline-offset-2 hover:underline ml-auto" onClick={cancelMfa}>Cancel</button>
              </div>
            </div>
          ) : (
          <>
          <div className="flex items-center gap-2 mb-2">
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} data-testid="login-card-logo" className="h-8 w-auto max-w-[110px] rounded-lg object-contain" />
            ) : (
              <Lightning size={22} weight="duotone" color="#FFD600" />
            )}
            <h2 className="text-2xl font-bold font-display">
              {mode === "login" ? "Welcome back" : "Create your Personal Account"}
            </h2>
          </div>
          <p className="text-[#000000] text-sm mb-5">
            {mode === "login" ? "Log in to your dashboard." : "Sign up for a Personal Account — no school needed."}
          </p>

          <form onSubmit={onCred} className="flex flex-col gap-3">
            {mode === "register" && (
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs uppercase tracking-[0.15em] text-[#000000] flex items-center gap-1.5">
                    <UserIcon size={14} /> First name
                  </span>
                  <input
                    data-testid="register-first-name-input"
                    className="input-base" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Jordan" required
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-xs uppercase tracking-[0.15em] text-[#000000]">Last name</span>
                  <input
                    data-testid="register-last-name-input"
                    className="input-base" value={lastName} onChange={(e) => setLastName(e.target.value)}
                    placeholder="Reyes"
                  />
                </label>
              </div>
            )}
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.15em] text-[#000000] flex items-center gap-1.5">
                <EnvelopeSimple size={14} /> Email
              </span>
              <input
                data-testid="auth-email-input"
                className="input-base" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@elevation650.com" required={mode === "register" || !code.trim()} autoComplete="email"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.15em] text-[#000000] flex items-center gap-1.5">
                <Lock size={14} /> Password
              </span>
              <PasswordInput
                testid="auth-password-input"
                className="input-base" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required={mode === "register" || !code.trim()} minLength={password ? 6 : undefined}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
              />
            </label>
            {mode === "login" && (
              <div className="mt-1">
                <p className="text-sm font-bold text-black" data-testid="classroom-student-note">
                  Classroom Student? Enter your code here
                </p>
                <input
                  data-testid="student-code-input"
                  className="input-base font-mono tracking-[0.3em] uppercase text-center text-lg mt-1.5 w-full"
                  value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="ABC123" maxLength={10}
                />
                {code.trim() && (
                  <p className="text-[11px] text-[#000000] mt-1" data-testid="student-code-hint">
                    No email or password needed — just hit Log in.
                  </p>
                )}
              </div>
            )}
            {mode === "login" && (
              <div className="-mt-1">
                <button type="button" data-testid="forgot-password-toggle"
                  className="btn-ghost text-xs text-[#000000] underline-offset-2 hover:underline p-0"
                  onClick={() => { setPwForgotOpen((v) => !v); setPwForgotMsg(""); setFHints(null); setPwForgotEmail(email); setForgotMode(code.trim() ? "student" : "email"); }}>
                  Forgot password or code?
                </button>
                <p className="text-[11px] text-[#000000] mt-1">Public users: your username is your email address.</p>
                {pwForgotOpen && (
                  <div className="mt-2 rounded-xl border border-[#C4C8CE] bg-[#F4F4F5] p-3" data-testid="forgot-password-form">
                    <div className="flex gap-2 mb-2">
                      <button type="button" data-testid="forgot-mode-email"
                        onClick={() => { setForgotMode("email"); setFHints(null); setPwForgotMsg(""); }}
                        className={"text-xs px-2.5 py-1 rounded-full border transition-colors " + (forgotMode === "email" ? "bg-[#0A0D14] text-white border-[#0A0D14]" : "border-[#C4C8CE] text-[#000000]")}>
                        I use email
                      </button>
                      <button type="button" data-testid="forgot-mode-student"
                        onClick={() => { setForgotMode("student"); setPwForgotMsg(""); }}
                        className={"text-xs px-2.5 py-1 rounded-full border transition-colors " + (forgotMode === "student" ? "bg-[#0A0D14] text-white border-[#0A0D14]" : "border-[#C4C8CE] text-[#000000]")}>
                        Classroom student
                      </button>
                    </div>
                    {forgotMode === "email" ? (
                      <>
                        <p className="text-xs text-[#000000] mb-2">Enter your email and we'll send a reset link (valid 1 hour).</p>
                        <div className="flex gap-2">
                          <input data-testid="forgot-password-email" type="email" className="input-base flex-1"
                            placeholder="you@elevation650.com" value={pwForgotEmail}
                            onChange={(e) => setPwForgotEmail(e.target.value)} />
                          <button type="button" data-testid="forgot-password-submit" onClick={onForgotPassword}
                            disabled={pwForgotBusy || !pwForgotEmail.trim()} className="btn-secondary whitespace-nowrap">
                            {pwForgotBusy ? "Sending…" : "Send link"}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-[#000000] mb-2">Enter your first and last name and we'll show a hint of your code. Your teacher has the full code.</p>
                        <div className="flex gap-2 flex-wrap">
                          <input data-testid="forgot-code-first" className="input-base flex-1 min-w-[110px]" placeholder="First name"
                            value={fFirst} onChange={(e) => setFFirst(e.target.value)} />
                          <input data-testid="forgot-code-last" className="input-base flex-1 min-w-[110px]" placeholder="Last name"
                            value={fLast} onChange={(e) => setFLast(e.target.value)} />
                          <button type="button" data-testid="forgot-code-submit" onClick={onForgotCode}
                            disabled={pwForgotBusy || !fFirst.trim() || !fLast.trim()} className="btn-secondary whitespace-nowrap">
                            {pwForgotBusy ? "Finding…" : "Find"}
                          </button>
                        </div>
                        {fHints && (
                          <div className="mt-2 text-xs text-[#000000]" data-testid="forgot-code-result">
                            {fHints.matches?.length ? (
                              <>
                                <p className="mb-1">{fHints.message}</p>
                                {fHints.matches.map((m, i) => (
                                  <div key={i} className="font-mono text-sm text-[#000000]">{m.masked_id} <span className="text-[#000000]">· {m.grade} · {m.class_name}</span></div>
                                ))}
                              </>
                            ) : <p>{fHints.message}</p>}
                          </div>
                        )}
                      </>
                    )}
                    {pwForgotMsg && <div data-testid="forgot-password-msg" className="text-xs text-[#166534] mt-2">{pwForgotMsg}</div>}
                  </div>
                )}
              </div>
            )}
            {err && <div data-testid="auth-error" className="text-[#FF5100] text-sm">{err}</div>}
            <button
              data-testid={mode === "login" ? "credential-login-button" : "register-button"}
              type="submit" className="btn-primary mt-2 flex items-center justify-center gap-2" disabled={loading}
            >
              {loading ? "Loading..." : (mode === "login" ? "Log in" : "Sign up")}
              <ArrowRight size={18} weight="bold" />
            </button>
          </form>

          {mode === "login" && localStorage.getItem("saproject:passkey-enrolled") && (
            <button
              data-testid="passkey-login-button"
              className="w-full mt-3 py-2.5 rounded-full font-display font-bold text-sm flex items-center justify-center gap-2 cursor-pointer"
              style={{ background: "transparent", color: "#CCFF00", border: "1.5px solid #CCFF00" }}
              onClick={async () => {
                try { const u = await passkeyLogin(); if (u) nav(from ? `${from.pathname}${from.search || ""}` : "/dashboard", { replace: true }); }
                catch (e2) { setErr(e2.message); }
              }}
            >
              <Fingerprint size={18} weight="bold" /> Sign in with fingerprint
            </button>
          )}

          <div className="text-center text-sm mt-4 text-[#000000]">
            {mode === "login" ? (
              <>New here?{" "}
                <button data-testid="switch-to-register-button" className="btn-ghost text-[#CCFF00] underline-offset-2 hover:underline" onClick={() => setMode("register")}>Create an account</button>
              </>
            ) : (
              <>Already have an account?{" "}
                <button data-testid="switch-to-login-button" className="btn-ghost text-[#CCFF00] underline-offset-2 hover:underline" onClick={() => setMode("login")}>Log in</button>
              </>
            )}
          </div>
          </>
          )}
        </div>
      </motion.div>
    </div>
    </div>
  );
}
